import { useState, useEffect } from 'react'
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { User, Lock, Power, Edit, Bell, Briefcase, MapPin, Phone, Mail, Shield, Clock, FileText, Award } from "react-feather";
import { Card, CardBody, CardHeader, Row, Col, Container, Nav, NavItem, NavLink, TabContent, TabPane, Progress } from "reactstrap";

// Custom Component
import NavbarComponent from "../components/layouts/Navbar";
import Footer from '../components/layouts/Footer'
import EditProfileModal from '../components/modal/EditProfileModal';
import AccessCodeModal from '../components/modal/AccessCodeModal';
import ChangePasswordModal from '../components/modal/ChangePasswordModal';
import NotificationPreferences from '../components/notifications/NotificationPreferences';

// Redux-Functions
import { submissionId } from '../../redux/slices/submission.Slice';
import { selectLanguageUse } from '../../redux/slices/language.Slice';

// Utility Service
import { getToken, getUser } from "../../utility/AuthService";

// Images
import ProfileIcon from '../../assets/images/profile/ProfileIcon.svg';

// API Service
import { getImageIndicatorService } from '../../services/indicatorService';
import { logoutService } from '../../services/authService';

const ProfilePage = () => {
    const authToken = getToken()
    const user = getUser()

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const languageUse = useSelector(selectLanguageUse || user?.language);

    const [activeTab, setActiveTab] = useState('1');
    const [editProfilemodal, setEditProfilemodal] = useState(false);
    const [changePasswordModal, setChangePasswordModal] = useState(false);
    const [accessCodeModal, setAccessCodeModal] = useState(false);
    const [profileImage, setProfileImage] = useState();
    const [profileCompletion, setProfileCompletion] = useState(0);

    useEffect(() => {
        // Calculate profile completion percentage
        const requiredFields = ['username', 'email', 'company', 'companyReg', 'companyLocation', 'designation', 'mobile', 'industry', 'country', 'state', 'city', 'companySize'];
        const completedFields = requiredFields.filter(field => user[field]);
        setProfileCompletion((completedFields.length / requiredFields.length) * 100);
    }, [user]);

    /**
        * @function logout
        * @params
        * @description used to logout the user
    */
    const logOut = () => {
        logoutService();
        dispatch(submissionId());
        navigate("/login");
    };

    /**
        * @function handleEditProfileModal
        * @params
        * @description set the visibility of edit profile modal
    */
    const handleEditProfileModal = () => {
        setEditProfilemodal(!editProfilemodal)
    };

    /**
        * @function handleChangePasswordModal
        * @params
        * @description set the visibility of change password modal
    */
    const handleChangePasswordModal = () => {
        setChangePasswordModal(!changePasswordModal)
    };

    /**
        * @function handleAccessCodeModal
        * @params
        * @description set the visibility of access code modal
    */
    const handleAccessCodeModal = () => {
        setAccessCodeModal(!accessCodeModal)
    };

    const toggleTab = tab => setActiveTab(tab);

    /**
        * @function getImage
        * @param
        * @description  get profile image
    */
    const getImage = async () => {
        try {
            const result = await getImageIndicatorService({
                token: authToken,
                file: user?.file
            })
            const temp = `data:image/${user?.file?.filename?.split(".")[1]};base64, ${Buffer.from(result?.data, "base64").toString("base64")}`
            setProfileImage(temp)
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Something went wrong')
        }
    };

    useEffect(() => {
        if (user?.file) {
            getImage()
        }
    }, [user?.file]);

    return <>
        <div>
            <Toaster />
        </div>

        <NavbarComponent />

        <Container className="py-5">
            <Row>
                <Col lg="4" md="5">
                    <Card className="profile-sidebar mb-4">
                        <CardBody>
                            <div className="text-center mb-4">
                                <div className="position-relative d-inline-block">
                                    <img 
                                        src={profileImage || ProfileIcon} 
                                        alt="Profile" 
                                        className="rounded-circle mb-3" 
                                        style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                                    />
                                    <button 
                                        className="btn btn-sm btn-primary position-absolute bottom-0 end-0"
                                        onClick={handleEditProfileModal}
                                    >
                                        <Edit size={14} />
                                    </button>
                                </div>
                                <h4 className="mb-1">{user?.username}</h4>
                                <p className="text-muted mb-3">{user?.designation || 'No designation set'}</p>
                                
                                <div className="profile-completion mb-4">
                                    <div className="d-flex justify-content-between mb-2">
                                        <small>{languageUse === 'English' ? 'Profile Completion' : 'Achèvement du profil'}</small>
                                        <small>{Math.round(profileCompletion)}%</small>
                                    </div>
                                    <Progress value={profileCompletion} className="progress-sm" />
                                </div>
                            </div>

                            <div className="profile-info">
                                <div className="info-item mb-3">
                                    <Briefcase size={16} className="me-2 text-primary" />
                                    <span>{user?.company || 'No company set'}</span>
                                </div>
                                <div className="info-item mb-3">
                                    <MapPin size={16} className="me-2 text-primary" />
                                    <span>{user?.companyLocation || 'No location set'}</span>
                                </div>
                                <div className="info-item mb-3">
                                    <Phone size={16} className="me-2 text-primary" />
                                    <span>{user?.mobile || 'No phone set'}</span>
                                </div>
                                <div className="info-item mb-3">
                                    <Mail size={16} className="me-2 text-primary" />
                                    <span>{user?.email}</span>
                                </div>
                            </div>

                            <div className="profile-actions mt-4">
                                <button className="btn btn-primary w-100 mb-2" onClick={handleEditProfileModal}>
                                    <Edit size={16} className="me-2" />
                                    {languageUse === 'English' ? 'Edit Profile' : 'Modifier le profil'}
                                </button>
                                <button className="btn btn-outline-primary w-100 mb-2" onClick={handleChangePasswordModal}>
                                    <Lock size={16} className="me-2" />
                                    {languageUse === 'English' ? 'Change Password' : 'Changer le mot de passe'}
                                </button>
                                <button className="btn btn-outline-primary w-100 mb-2" onClick={handleAccessCodeModal}>
                                    <Shield size={16} className="me-2" />
                                    {languageUse === 'English' ? 'Access Code' : 'Code d\'accès'}
                                </button>
                                <button className="btn btn-outline-danger w-100" onClick={logOut}>
                                    <Power size={16} className="me-2" />
                                    {languageUse === 'English' ? 'Logout' : 'Déconnexion'}
                                </button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                <Col lg="8" md="7">
                    <Card>
                        <CardHeader className="border-bottom">
                            <Nav tabs className="nav-tabs-line">
                                <NavItem>
                                    <NavLink
                                        className={activeTab === '1' ? 'active' : ''}
                                        onClick={() => toggleTab('1')}
                                    >
                                        <Bell size={18} className="me-2" />
                                        {languageUse === 'English' ? 'Notifications' : 'Notifications'}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={activeTab === '2' ? 'active' : ''}
                                        onClick={() => toggleTab('2')}
                                    >
                                        <FileText size={18} className="me-2" />
                                        {languageUse === 'English' ? 'Submissions' : 'Soumissions'}
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={activeTab === '3' ? 'active' : ''}
                                        onClick={() => toggleTab('3')}
                                    >
                                        <Award size={18} className="me-2" />
                                        {languageUse === 'English' ? 'Achievements' : 'Réalisations'}
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </CardHeader>

                        <CardBody>
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <NotificationPreferences />
                                </TabPane>
                                <TabPane tabId="2">
                                    <div className="submissions-history">
                                        {/* Add submissions history component here */}
                                        <p className="text-muted text-center py-5">
                                            {languageUse === 'English' ? 'No submissions yet' : 'Pas encore de soumissions'}
                                        </p>
                                    </div>
                                </TabPane>
                                <TabPane tabId="3">
                                    <div className="achievements">
                                        {/* Add achievements component here */}
                                        <p className="text-muted text-center py-5">
                                            {languageUse === 'English' ? 'No achievements yet' : 'Pas encore de réalisations'}
                                        </p>
                                    </div>
                                </TabPane>
                            </TabContent>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>

        <Footer />

        {/* Edit Profile Modal */}
        <EditProfileModal modal={editProfilemodal} toggle={handleEditProfileModal} profileImage={profileImage} />

        {/* Change Password Modal */}
        <ChangePasswordModal modal={changePasswordModal} toggle={handleChangePasswordModal} />

        {/* Access Code Modal */}
        <AccessCodeModal modal={accessCodeModal} toggle={handleAccessCodeModal} />
    </>
};

export default ProfilePage